import { OperationalParamsPanel } from '@go-widgets/well-logs-widget';
import { EditLightSmall, SoundMaxLight, SoundMuteLight } from '@repo/go-icons';
import { Button, Tooltip } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useService } from 'src/packages/di';

import { NoWellChosen } from '../no-well-chosen';
import { WidgetHeader } from '../widget-header/WidgetHeader';
import type { TWidgetRendererOptions } from '../workspace/services/WidgetRendererService';

import type { OperationalParametersWidgetEntity } from './OperationalParametersWidget.entity';
import styles from './OperationalParametersWidget.module.scss';
import { OperationalParametersWidgetStore } from './OperationalParametersWidget.store';

type WrapperProps = {
  entity: OperationalParametersWidgetEntity;
  options: TWidgetRendererOptions;
};

type Props = {
  store: OperationalParametersWidgetStore;
  entity: OperationalParametersWidgetEntity;
  options: TWidgetRendererOptions;
};

export const OperationalParametersWidgetWrapper = observer(function OperationalParametersWidgetWrapper({
  entity,
  options,
}: WrapperProps) {
  const [store, setStore] = useState<OperationalParametersWidgetStore | null>(null);

  const storeService = useService('widgetStoreService');

  useEffect(() => {
    if (store) return;

    const _store = storeService.getStore<OperationalParametersWidgetEntity, OperationalParametersWidgetStore>(entity);

    if (_store) {
      setStore(_store);
      if (_store.groupSelectStore == null) {
        _store.setGroupSelectStore(options.groupSelectStore);
      }
      if (_store.wellListPanel == null) {
        _store.setWellListPanel(options.wellListEntity);
      }
    } else {
      const newStore = new OperationalParametersWidgetStore(entity, options.groupSelectStore, options.wellListEntity);

      storeService.setStore<OperationalParametersWidgetEntity, OperationalParametersWidgetStore>(entity, newStore);

      setStore(newStore);
    }
  }, [setStore, storeService, entity, options.groupSelectStore, store, options.wellListEntity]);

  if (!store) return null;

  return <OperationalParametersWidget store={store} entity={entity} options={options} />;
});

export const OperationalParametersWidget = observer(function OperationalParametersWidget({
  entity,
  options,
  store,
}: Props) {
  const { setGroupForWellLogs, isReady, effect, setEditOpen } = store;
  const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(null);

  const { t } = useTranslation();

  const { wellId, isFullscreen, state, enableSound, toggleWidgetSound, wellboreName } = entity;

  useEffect(effect, [effect]);

  const directories = useService('directories');
  const wells = directories.getObject('GOdashboard_Well');
  const wellName = wells?.find((item) => item.id === wellId)?.data.name ?? wellId;

  return (
    <div className={styles.widgetWrapper} ref={(ref) => setWrapperElement(ref)}>
      <WidgetHeader
        entity={entity}
        onWidgetChangeGroup={setGroupForWellLogs}
        groupSelectStore={options.groupSelectStore}
        widgetName={entity.title}
        wellName={wellName}
        wellboreName={wellboreName}
        onWidgetDelete={() => {
          options.onWidgetDelete(entity);
        }}
        showCloseButton={!isFullscreen}
        draggable={!isFullscreen}
        widgetNameClassName={styles.operationalPanelName}
        widgetTitleClassName={styles.operationalPanelTitle}
        renderAdditionalButtons={() => {
          if (!isReady) return null;

          return (
            <>
              <Tooltip title={t('common:Widget.sound')} delay={400}>
                <Button
                  onClick={() => toggleWidgetSound()}
                  variant="flat"
                  icon={
                    enableSound ? <SoundMuteLight className={styles.icon} /> : <SoundMaxLight className={styles.icon} />
                  }
                  className={styles.button}
                />
              </Tooltip>
              <Tooltip title={t('common:Widget.edit')} delay={400}>
                <Button
                  onClick={() => setEditOpen()}
                  variant="flat"
                  icon={<EditLightSmall className={styles.icon} />}
                  className={styles.button}
                />
              </Tooltip>
            </>
          );
        }}
      />
      {wrapperElement &&
        (state ? (
          <div className={styles.wrapper}>
            {state && <OperationalParamsPanel widgetState={state} externalContainer={wrapperElement} />}
          </div>
        ) : (
          <NoWellChosen />
        ))}
    </div>
  );
});
